import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import agent from "../api/agent";
import { ApiRequestStatus } from "../enums/apiRequestStatus";
import { SummaryDto } from "../models/pcs/summaryDto";
import { PCSSummaryDto } from "../models/pcs/pcsSummaryDto";
import { PCSDetailsDto } from "../models/pcs/pcsDetailsDto";
import { PcsEnrolmentYear } from "../models/pcs/pcsEnrolmentYear";
import { GetSchemeYearEnrolmentsTotalQuery } from "../../features/RandomSample/common/getSchemeYearEnrolmentsTotalQuery";
import { CreateRandomSampleCommand } from "../../features/RandomSample/common/createRandomSampleCommand";

interface PcsState {
    summaryTitles: string[] | null;
    summaryData: SummaryDto[][] | null;
    legacyTitles: string[] | null;
    legacySummaryData: SummaryDto[][] | null;
    pcsSummaryDto: PCSSummaryDto | null;
    registrationDetails: PCSDetailsDto | null;
    registrationDetailsList: PCSDetailsDto[] | null;
    pcsEnrolments: PcsEnrolmentYear[] | null;
    pcsEnrolmentsByMultiplePerson: PcsEnrolmentYear[] | null;
    pcsCurrentYear: number | null;
    schemeYearEnrolmentsTotal: number | null;
    isRandomSampleSubmitted: boolean | null;

    getCreditSummaryStatus: ApiRequestStatus;
    registerStateStatus: ApiRequestStatus;
    registrationDetailsListStatus: ApiRequestStatus;
    pcsEnrolmentsStatus: ApiRequestStatus;
    pcsCurrentYearStatus: ApiRequestStatus;
    getSchemeYearEnrolmentsTotalStatus: ApiRequestStatus;
    submitRandomSampleStatus: ApiRequestStatus;
    getPcsEnrolmentsByMultiplePersonIdsStatus: ApiRequestStatus; 
}

const initialState: PcsState = {
    summaryTitles: null,
    summaryData: null,
    legacyTitles: null,
    legacySummaryData: null,
    pcsSummaryDto: null,
    registrationDetails: null,
    registrationDetailsList: null,
    pcsEnrolments: null,
    pcsEnrolmentsByMultiplePerson: null,
    pcsCurrentYear: null,
    schemeYearEnrolmentsTotal: null,
    isRandomSampleSubmitted: null,

    getCreditSummaryStatus: ApiRequestStatus.Idle,
    registerStateStatus: ApiRequestStatus.Idle,
    registrationDetailsListStatus: ApiRequestStatus.Idle,
    pcsEnrolmentsStatus: ApiRequestStatus.Idle,
    pcsCurrentYearStatus: ApiRequestStatus.Idle,
    getSchemeYearEnrolmentsTotalStatus: ApiRequestStatus.Idle,
    submitRandomSampleStatus: ApiRequestStatus.Idle,
    getPcsEnrolmentsByMultiplePersonIdsStatus: ApiRequestStatus.Idle
}

export const getCreditSummaryAsync = createAsyncThunk<PCSSummaryDto, string>(
    'pcsSlice/getCreditSummaryAsync',
    async (personId, thunkAPI) => {
        try {
            return agent.pcs.getCreditSummaryAsync(personId);
        } catch (error: any) {
            return thunkAPI.rejectWithValue({ error: error.data });
        }
    }
)

export const getRegistrationDetailsAsync = createAsyncThunk<PCSDetailsDto, string>(
    'pcsSlice/getRegistrationDetailsAsync',
    async (personId, thunkAPI) => {
        try {
            return agent.pcs.getRegistrationDetailsAsync(personId);
        } catch (error: any) {
            return thunkAPI.rejectWithValue({ error: error.data });
        }
    }
)

export const getRegistrationDetailsByMultiplePersonIdsAsync = createAsyncThunk<PCSDetailsDto[], string[]>(
    'pcsSlice/getRegistrationDetailsByMultiplePersonIdsAsync',
    async (personIds, thunkAPI) => {
        try {
            return agent.pcs.getRegistrationDetailsByMultiplePersonIdsAsync(personIds);
        } catch (error: any) {
            return thunkAPI.rejectWithValue({ error: error.data });
        }
    }
)

export const getPcsEnrolmentsAsync = createAsyncThunk<PcsEnrolmentYear[], string>(
    'pcsSlice/getPcsEnrolmentsAsync',
    async (personId, thunkAPI) => {
        try {
            return agent.pcs.getPcsEnrolmentsAsync(personId);
        } catch (error: any) {
            return thunkAPI.rejectWithValue({ error: error.data });
        }
    }
)

export const getPcsEnrolmentsByMultiplePersonIdsAsync = createAsyncThunk<PcsEnrolmentYear[], string[]>(
    'pcsSlice/getPcsEnrolmentsByMultiplePersonIdsAsync',
    async (personIds, thunkAPI) => {
        try {
            return agent.pcs.getPcsEnrolmentsByMultiplePersonIdsAsync(personIds);
        } catch (error: any) {
            return thunkAPI.rejectWithValue({ error: error.data });
        }
    }
)

export const getPcsCurrentYearAsync = createAsyncThunk<number>(
    'pcsSlice/getPcsCurrentYearAsync',
    async (_, thunkAPI) => {
        try {
            return agent.pcs.getPcsCurrentYearAsync();
        } catch (error: any) {
            return thunkAPI.rejectWithValue({ error: error.data });
        }
    }
)

export const getSchemeYearEnrolmentsTotalAsync = createAsyncThunk<number, GetSchemeYearEnrolmentsTotalQuery>(
    'pcsSlice/getSchemeYearEnrolmentsTotalAsync',
    async (getSchemeYearEnrolmentsTotalQuery, thunkAPI) => {
        try {
            return agent.pcs.getSchemeYearEnrolmentsTotalAsync(getSchemeYearEnrolmentsTotalQuery);
        } catch (error: any) {
            return thunkAPI.rejectWithValue({ error: error.data });
        }
    }
)

export const submitRandomSampleAsync = createAsyncThunk<boolean, CreateRandomSampleCommand>(
    'pcsSlice/submitRandomSampleAsync',
    async (createRandomSampleCommand, thunkAPI) => {
        try {
            return agent.pcs.submitRandomSampleAsync(createRandomSampleCommand);
        } catch (error: any) {
            return thunkAPI.rejectWithValue({ error: error.data });
        }
    }
)

export const pcsSlice = createSlice({
    name: 'pcsSlice',
    initialState,
    reducers: {

    },
    extraReducers: (builder => {
        builder.addCase(getCreditSummaryAsync.pending, (state) => {
            state.getCreditSummaryStatus = ApiRequestStatus.Pending;
        });
        builder.addCase(getCreditSummaryAsync.fulfilled, (state, action) => {
            state.getCreditSummaryStatus = ApiRequestStatus.Fulfilled;
            state.summaryTitles = action.payload.titles;
            state.summaryData = action.payload.summaryData;
            state.legacyTitles = action.payload.legacyTitles;
            state.legacySummaryData = action.payload.legacySummaryData;
            state.pcsSummaryDto = action.payload;
        });
        builder.addCase(getCreditSummaryAsync.rejected, (state) => {
            state.getCreditSummaryStatus = ApiRequestStatus.Rejected;
        });

        builder.addCase(getRegistrationDetailsAsync.pending, (state) => {
            state.registerStateStatus = ApiRequestStatus.Pending;
        });
        builder.addCase(getRegistrationDetailsAsync.fulfilled, (state, action) => {
            state.registrationDetails = action.payload;
            state.registerStateStatus = ApiRequestStatus.Fulfilled;
        });
        builder.addCase(getRegistrationDetailsAsync.rejected, (state) => {
            state.registerStateStatus = ApiRequestStatus.Rejected;
        });

        builder.addCase(getRegistrationDetailsByMultiplePersonIdsAsync.pending, (state) => {
            state.registrationDetailsListStatus = ApiRequestStatus.Pending;
        });
        builder.addCase(getRegistrationDetailsByMultiplePersonIdsAsync.fulfilled, (state, action) => {
            state.registrationDetailsList = action.payload;
            state.registrationDetailsListStatus = ApiRequestStatus.Fulfilled;
        });
        builder.addCase(getRegistrationDetailsByMultiplePersonIdsAsync.rejected, (state) => {
            state.registrationDetailsListStatus = ApiRequestStatus.Rejected;
        });

        builder.addCase(getPcsEnrolmentsAsync.pending, (state) => {
            state.pcsEnrolmentsStatus = ApiRequestStatus.Pending;
        });
        builder.addCase(getPcsEnrolmentsAsync.fulfilled, (state, action) => {
            state.pcsEnrolments = action.payload;
            state.pcsEnrolmentsStatus = ApiRequestStatus.Fulfilled;
        });
        builder.addCase(getPcsEnrolmentsAsync.rejected, (state) => {
            state.pcsEnrolmentsStatus = ApiRequestStatus.Rejected;
        });

        builder.addCase(getPcsEnrolmentsByMultiplePersonIdsAsync.pending, (state) => {
            state.getPcsEnrolmentsByMultiplePersonIdsStatus = ApiRequestStatus.Pending;
        });
        builder.addCase(getPcsEnrolmentsByMultiplePersonIdsAsync.fulfilled, (state, action) => {
            state.pcsEnrolmentsByMultiplePerson = action.payload;
            state.getPcsEnrolmentsByMultiplePersonIdsStatus = ApiRequestStatus.Fulfilled;
        });
        builder.addCase(getPcsEnrolmentsByMultiplePersonIdsAsync.rejected, (state) => {
            state.getPcsEnrolmentsByMultiplePersonIdsStatus = ApiRequestStatus.Rejected;
        });

        builder.addCase(getPcsCurrentYearAsync.pending, (state) => {
            state.pcsCurrentYearStatus = ApiRequestStatus.Pending;
        });
        builder.addCase(getPcsCurrentYearAsync.fulfilled, (state, action) => {
            state.pcsCurrentYearStatus = ApiRequestStatus.Fulfilled;
            state.pcsCurrentYear = action.payload;
        });
        builder.addCase(getPcsCurrentYearAsync.rejected, (state) => {
            state.pcsCurrentYearStatus = ApiRequestStatus.Rejected;
        });

        builder.addCase(getSchemeYearEnrolmentsTotalAsync.pending, (state) => {
            state.getSchemeYearEnrolmentsTotalStatus = ApiRequestStatus.Pending;
        });
        builder.addCase(getSchemeYearEnrolmentsTotalAsync.fulfilled, (state, action) => {
            state.getSchemeYearEnrolmentsTotalStatus = ApiRequestStatus.Fulfilled;
            state.schemeYearEnrolmentsTotal = action.payload;
        });
        builder.addCase(getSchemeYearEnrolmentsTotalAsync.rejected, (state) => {
            state.getSchemeYearEnrolmentsTotalStatus = ApiRequestStatus.Rejected;
        });

        builder.addCase(submitRandomSampleAsync.pending, (state) => {
            state.submitRandomSampleStatus = ApiRequestStatus.Pending;
        });
        builder.addCase(submitRandomSampleAsync.fulfilled, (state, action) => {
            state.submitRandomSampleStatus = ApiRequestStatus.Fulfilled;
            state.isRandomSampleSubmitted = action.payload;
        });
        builder.addCase(submitRandomSampleAsync.rejected, (state) => {
            state.submitRandomSampleStatus = ApiRequestStatus.Rejected;
        });
    })
})
